import {
    Box,
    Button,
    Checkbox,
    Menu,
    MenuItem,
    Chip,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import { MouseEvent, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/DataTable/DataTable';
import TextChip from '../../../components/TextChip';
import { getDate } from '../../../utils/common';
import { SiniestroItem, SiniestroStatus, VisitaRoamItem } from '../types/siniestros.types';
import {
    ORGANIZATION_LEGEND_CONFIG,
    resolveOrganizationLegendName
} from '../../../constants/organizationLegends';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import {
    useCancelarSiniestroMutation,
    useGetMotivosCancelacionSiniestroQuery
} from '../../../services/roamApi';
import { useRoles } from '../../../hooks/useRoles';
import useRoutePrefix from '../../../hooks/useRoutePrefix';
import ChipVencimientoSRT from './ChipVencimientoSRT';
import SrtStatusIcon from './SrtStatusIcon';
import { renderStateChipSRT } from '../../GeneracionArchivosSRT/RGRL/components/StateChipSRT';
import { SRT_STATES } from '../../GeneracionArchivosSRT/consts/states';
import { SrtStatusEnum } from './SiniestrosFilters/constants';
import TableActionButton from '../../Presentaciones/components/TableActionButton';
import { AlertDialog } from '../../../components/Alert/AlertDialog';
import { VisitaEstado, visitaEstadoChipMap } from '../../Visitas/consts/visitasStatus';
import { getVisitaDisplayConfig } from '../../Visitas/utils/visitasUtils';
import ExpressVisitManagementDrawer from './ExpressVisitManagementDrawer';
import { SiniestroHistoryDrawer } from './SiniestroHistoryDrawer';
import { getContratoOrganismoPreventor } from '../utils/contratoOrganismoPreventor';
import { getVisitaPresentada } from '../utils/visitaPresentada';
import { tieneLugarOcurrenciaCompleto } from '../utils/lugarOcurrencia';

const ESTADO_SINIESTRO_CHIP_CONFIG: Record<
    SiniestroStatus,
    { activeColor: string; activeBackgroundColor: string; borderColor: string }
> = {
    ALL: { activeColor: '#616161', activeBackgroundColor: '#F5F5F5', borderColor: '#BDBDBD' },
    OPEN: { activeColor: '#0D47A1', activeBackgroundColor: '#E3F2FD', borderColor: '#90CAF9' },
    PENDING: { activeColor: '#E65100', activeBackgroundColor: '#FFF3E0', borderColor: '#FFCC80' },
    FINALIZED: { activeColor: '#1B5E20', activeBackgroundColor: '#E8F5E9', borderColor: '#A5D6A7' },
    CANCELLED: { activeColor: '#616161', activeBackgroundColor: '#F5F5F5', borderColor: '#BDBDBD' }
};

const renderEstadoChip = (item: SiniestroItem) => {
    const config = ESTADO_SINIESTRO_CHIP_CONFIG[item.estado] || ESTADO_SINIESTRO_CHIP_CONFIG.PENDING;
    return (
        <TextChip
            label={item?.estadoLabel || '-'}
            disabled={false}
            onClick={() => {}}
            activeColor={config.activeColor}
            activeBackgroundColor={config.activeBackgroundColor}
            activeHoverColor={config.activeBackgroundColor}
            borderColor={config.borderColor}
            borderRadius="999px"
        />
    );
};

interface SiniestrosTableProps {
    filters: Record<string, any>;
    setFilters: (value: any) => void;
    siniestros: SiniestroItem[];
    isLoading: boolean;
    count: number;
    canViewDetail: boolean;
    canCancel: boolean;
    canCreateVisit: boolean;
    activeTab: SiniestroStatus;
    onRefresh: () => void;
    t: (key: string, options?: Record<string, any>) => string;
    selectedIds: Set<number>;
    seleccionEstado: 'none' | 'some' | 'all';
    isSeleccionable: (siniestro: SiniestroItem) => boolean;
    handleToggleSeleccion: (id: number, siniestro: SiniestroItem) => void;
    handleSelectAll: () => void;
    handleClearSeleccion: () => void;
    handleDescargarZip?: (siniestro: SiniestroItem) => Promise<void>;
    isLoadingZip?: boolean;
    // Tab Siniestros de Gestión de Archivos SRT: descargar el TXT y un menú con el detalle, el
    // informe de investigación y el acta, sin las columnas Preventor, Visitas ROAM y Estado (SMP-561)
    archivosSrt?: {
        onDescargarTxt: (siniestro: SiniestroItem) => void;
        isDescargando: boolean;
        detalleState?: Record<string, any>;
        onDescargarInforme: (siniestro: SiniestroItem) => void;
        onDescargarActa: (siniestro: SiniestroItem) => void;
    };
}

// En Gestión de Archivos SRT el estado se muestra con el chip de las tabs RGRL y Visitas:
// Cargado está pendiente de generar y Exportado ya se generó
const renderEstadoGeneracionArchivo = (estadoSrt?: number) =>
    estadoSrt === SrtStatusEnum.EXPORTADO
        ? renderStateChipSRT(SRT_STATES.GENERATED, (key) => key)
        : renderStateChipSRT(SRT_STATES.PENDING, (key) => key);

const getVisitChipColors = (visita: VisitaRoamItem) => {
    const statusConfig = visita.estadoId
        ? visitaEstadoChipMap[visita.estadoId as VisitaEstado]
        : undefined;

    if (statusConfig) {
        return {
            activeColor: statusConfig.textColor,
            activeBackgroundColor: statusConfig.backgroundColor,
            activeHoverColor: statusConfig.backgroundColor,
            borderColor: statusConfig.borderColor || statusConfig.textColor
        };
    }

    if (visita.badgeTone === 'expired') {
        return {
            activeColor: '#D32F2F',
            activeBackgroundColor: '#FDECEC',
            activeHoverColor: '#F8D7D7',
            borderColor: '#E8A0A0'
        };
    }

    if (visita.badgeTone === 'urgent') {
        return {
            activeColor: '#ED6C02',
            activeBackgroundColor: '#FFF4E5',
            activeHoverColor: '#FFE2BF',
            borderColor: '#F1B27A'
        };
    }

    return {
        activeColor: '#476773',
        activeBackgroundColor: '#EEF5F6',
        activeHoverColor: '#DDEBED',
        borderColor: '#9CB4BB'
    };
};

const getVisitTooltip = (
    visita: VisitaRoamItem,
    t: (key: string, options?: Record<string, any>) => string
) => {
    const statusConfig = visita.estadoId
        ? visitaEstadoChipMap[visita.estadoId as VisitaEstado]
        : undefined;
    const displayConfig = getVisitaDisplayConfig(visita.estadoId ?? 0, visita.motivoFalla ?? null);
    const esFallida = displayConfig.color === 'ambar';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, py: 0.5, minWidth: 280 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    pr: 0.5
                }}
            >
                <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                    {t('table.visitTooltip.visit')} {visita.id}
                </Typography>
                {statusConfig && (
                    <Chip
                        label={statusConfig.text}
                        size="small"
                        sx={{
                            fontWeight: 700,
                            color: statusConfig.textColor,
                            backgroundColor: statusConfig.backgroundColor,
                            borderRadius: '999px',
                            border: `1px solid ${statusConfig.borderColor || statusConfig.textColor}`,
                            height: 22,
                            ml: 'auto'
                        }}
                    />
                )}
            </Box>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                {t('table.visitTooltip.preventor')}: {visita.preventor || '-'}
            </Typography>
            {esFallida && (
                <Typography variant="body2" sx={{ color: '#FFB74D', fontWeight: 600 }}>
                    {displayConfig.tooltip}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: visita.fechaRealizada
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'minmax(0, 1fr)',
                    gap: 1.5
                }}
            >
                <Box>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.72)', display: 'block', mb: 0.25 }}
                    >
                        {t('table.visitTooltip.due')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                        {visita.fechaVencimiento ? getDate(visita.fechaVencimiento) : '-'}
                    </Typography>
                </Box>
                {visita.fechaRealizada && (
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.72)', display: 'block', mb: 0.25 }}
                        >
                            {t('table.visitTooltip.completed')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                            {getDate(visita.fechaRealizada)}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const renderVisits = (
    visitas: VisitaRoamItem[],
    t: (key: string, options?: Record<string, any>) => string
) => {
    if (!visitas?.length) {
        return <Typography variant="body2">{t('table.visitTooltip.empty')}</Typography>;
    }

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
            {visitas.slice(0, 3).map((visita) => {
                const esFallida =
                    getVisitaDisplayConfig(visita.estadoId ?? 0, visita.motivoFalla ?? null)
                        .color === 'ambar';
                const chip = (
                    <TextChip
                        label={visita.badgeLabel || '-'}
                        disabled={false}
                        onClick={() => {}}
                        {...getVisitChipColors(visita)}
                        borderColor={getVisitChipColors(visita).borderColor}
                        borderRadius="999px"
                    />
                );

                return (
                    <Tooltip
                        key={visita.id || visita.nro}
                        title={getVisitTooltip(visita, t)}
                        placement="top"
                        arrow
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {chip}
                            {esFallida && (
                                <WarningAmberIcon sx={{ fontSize: 14, color: '#ED6C02' }} />
                            )}
                        </Box>
                    </Tooltip>
                );
            })}
            {visitas.length > 3 && (
                <TextChip
                    label={`+${visitas.length - 3}`}
                    disabled={false}
                    onClick={() => {}}
                    activeColor="#476773"
                    activeBackgroundColor="#EEF5F6"
                    activeHoverColor="#DDEBED"
                    borderColor="#9CB4BB"
                    borderRadius="999px"
                />
            )}
        </Stack>
    );
};

const getMainPreventor = (item: SiniestroItem) => {
    const preventors = (item?.visitasRoam || [])
        .map((visita) => visita?.preventor?.trim())
        .filter(Boolean) as string[];

    if (!preventors.length) {
        return item?.preventorPrincipal || '-';
    }

    const counts = new Map<string, number>();
    preventors.forEach((name) => {
        counts.set(name, (counts.get(name) || 0) + 1);
    });

    let selected = preventors[0];
    let maxCount = counts.get(selected) || 0;

    counts.forEach((count, name) => {
        if (count > maxCount) {
            selected = name;
            maxCount = count;
        }
    });

    return selected || '-';
};

const buildTableItems = (siniestros: SiniestroItem[]) => ({
    data: {
        data: siniestros
    }
});

// CUIT debajo del nombre (o del número) en las columnas Contrato y Organismo.
const renderCuit = (cuit?: string) =>
    cuit ? (
        <Box>
            <Typography variant="body2">{cuit}</Typography>
        </Box>
    ) : undefined;

function SiniestrosTable({
    filters,
    setFilters,
    siniestros,
    isLoading,
    count,
    canViewDetail,
    canCancel,
    canCreateVisit,
    activeTab,
    onRefresh,
    t,
    selectedIds,
    seleccionEstado,
    isSeleccionable,
    handleToggleSeleccion,
    handleSelectAll,
    handleClearSeleccion,
    handleDescargarZip,
    isLoadingZip = false,
    archivosSrt
}: SiniestrosTableProps) {
    const navigate = useNavigate();
    const { isAdminBO, isViewerBO, isPreventor, isSupervisorSIMP } = useRoles();
    const isBOUser = isAdminBO || isViewerBO;
    const routePrefix = useRoutePrefix();
    const [cancelarSiniestro, { isLoading: isCancelling }] = useCancelarSiniestroMutation();
    const { data: cancelReasonOptions = [] } = useGetMotivosCancelacionSiniestroQuery(undefined, {
        skip: isSupervisorSIMP || !!archivosSrt
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(
        null
    );
    const [menuOpen, setMenuOpen] = useState(false);
    const [selectedSiniestro, setSelectedSiniestro] = useState<SiniestroItem | null>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [expressDrawerOpen, setExpressDrawerOpen] = useState(false);
    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState<number | ''>('');
    const [cancelReasonError, setCancelReasonError] = useState(false);

    const handleClickAction = (event: MouseEvent<HTMLElement>, item: SiniestroItem) => {
        const rect = event.currentTarget.getBoundingClientRect();

        setSelectedSiniestro(item);
        setAnchorEl(event.currentTarget);
        setAnchorPosition({
            top: Math.round(rect.bottom),
            left: Math.round(rect.right)
        });
        setMenuOpen(true);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setAnchorPosition(null);
        setMenuOpen(false);
    };

    const handleOpenCancelDialog = () => {
        handleCloseMenu();
        setCancelReason('');
        setCancelReasonError(false);
        setCancelDialogOpen(true);
    };

    const handleCloseCancelDialog = () => {
        setCancelDialogOpen(false);
        setCancelReason('');
        setCancelReasonError(false);
    };

    const handleOpenExpressDrawer = (item: SiniestroItem) => {
        setSelectedSiniestro(item);
        setExpressDrawerOpen(true);
    };

    const handleCloseExpressDrawer = () => {
        setExpressDrawerOpen(false);
    };

    const handleOpenHistoryDrawer = () => {
        handleCloseMenu();
        setHistoryDrawerOpen(true);
    };

    const handleCloseHistoryDrawer = () => {
        setHistoryDrawerOpen(false);
    };

    const canOpenExpressDrawer = (item: SiniestroItem) => {
        return (item?.visitasRoam?.length || 0) > 0;
    };

    const handleCancelSiniestro = async () => {
        if (!selectedSiniestro?.id) return;

        if (!cancelReason) {
            setCancelReasonError(true);
            return;
        }

        try {
            await cancelarSiniestro({
                id: Number(selectedSiniestro.id),
                body: { idMotivoCancelacion: cancelReason }
            }).unwrap();
            enqueueSnackbar(t('table.alerts.cancelSuccess'), { variant: 'success' });
            handleCloseCancelDialog();
            setSelectedSiniestro(null);
        } catch (error: any) {
            enqueueSnackbar(error?.data?.message || t('table.alerts.cancelError'), {
                variant: 'error'
            });
        }
    };

    const handleCreateVisitFromTable = async () => {
        if (!selectedSiniestro?.id) return;

        if (!tieneLugarOcurrenciaCompleto(selectedSiniestro)) {
            enqueueSnackbar(t('detail.occurrenceLocation.alerts.validVisitRequired'), {
                variant: 'warning'
            });
            handleCloseMenu();
            return;
        }

        handleCloseMenu();
        navigate(`/${routePrefix}/visitas/crear`, {
            state: {
                idSiniestro: Number(selectedSiniestro.id),
                nroSiniestro: selectedSiniestro.nroSiniestro || selectedSiniestro.id,
                idEstablecimiento: selectedSiniestro.idEstablecimiento || null,
                establishmentName: selectedSiniestro.establecimientoNombre || null,
                occurrenceLocation: {
                    calle: selectedSiniestro.lugarOcurrencia?.calle || '',
                    altura: selectedSiniestro.lugarOcurrencia?.altura || '',
                    piso: selectedSiniestro.lugarOcurrencia?.piso || '',
                    depto: selectedSiniestro.lugarOcurrencia?.depto || '',
                    codigoPostal: selectedSiniestro.lugarOcurrencia?.codigoPostal || '',
                    localidad:
                        selectedSiniestro.lugarOcurrencia?.localidad?.nombre ||
                        (selectedSiniestro.establecimiento?.direccion?.localidad?.id ===
                        selectedSiniestro.lugarOcurrencia?.idLocalidad
                            ? selectedSiniestro.establecimiento?.direccion?.localidad?.nombre || ''
                            : ''),
                    provincia:
                        selectedSiniestro.lugarOcurrencia?.localidad?.provincia?.nombre ||
                        (selectedSiniestro.establecimiento?.direccion?.localidad?.id ===
                        selectedSiniestro.lugarOcurrencia?.idLocalidad
                            ? selectedSiniestro.establecimiento?.direccion?.localidad?.provincia
                                  ?.nombre || ''
                            : ''),
                    idLocalidad:
                        selectedSiniestro.lugarOcurrencia?.localidad?.id ||
                        selectedSiniestro.lugarOcurrencia?.idLocalidad ||
                        null
                },
                idTipoSiniestro: selectedSiniestro?.idTipoSiniestro ?? null,
                fechaAltaRoam: selectedSiniestro?.fechaAltaRoam ?? null,
                patologiasTrazadoras: selectedSiniestro?.patologiasTrazadoras ?? null
            }
        });
    };

    return (
        <>
            <DataTable
                columnTitles={[
                    // El supervisor solo consulta: sin selección para Generar TXT ni
                    // Preventor principal (SMP-460)
                    ...(isSupervisorSIMP
                        ? []
                        : [
                              <Checkbox
                                  key="select-all"
                                  size="small"
                                  checked={seleccionEstado === 'all'}
                                  indeterminate={seleccionEstado === 'some'}
                                  onChange={() =>
                                      seleccionEstado === 'none' || seleccionEstado === 'some'
                                          ? handleSelectAll()
                                          : handleClearSeleccion()
                                  }
                              />
                          ]),
                    t('table.columns.siniestro'),
                    t('table.columns.employee'),
                    'Contrato',
                    ...(isPreventor ? ['Organismo'] : []),
                    t('table.columns.type'),
                    ...(isSupervisorSIMP || archivosSrt ? [] : [t('table.columns.mainPreventor')]),
                    ...(archivosSrt ? [] : [t('table.columns.visits')]),
                    t('table.columns.complaintDate'),
                    ...(isBOUser ? ['Vencimiento SRT'] : []),
                    ...(archivosSrt ? [] : [t('table.columns.status')]),
                    archivosSrt
                        ? t('table.columns.fileGenerationStatus')
                        : t('table.columns.srtStatus'),
                    t('table.columns.actions')
                ]}
                columnWidths={
                    archivosSrt
                        ? [
                              '4%',
                              '10%',
                              '18%',
                              ...(isPreventor ? ['14%', '14%'] : ['16%']),
                              '12%',
                              '10%',
                              ...(isBOUser ? ['10%'] : []),
                              '10%',
                              '10%'
                          ]
                        : isSupervisorSIMP
                          ? ['9%', '16%', '10%', '12%', '16%', '9%', '9%', '9%', '10%']
                          : isBOUser
                            ? ['4%', '7%', '12%', '7%', '7%', '11%', '8%', '8%', '8%', '8%', '8%']
                            : isPreventor
                              ? ['4%', '7%', '12%', '12%', '12%', '7%', '10%', '8%', '8%', '8%', '8%']
                              : ['4%', '8%', '14%', '8%', '8%', '14%', '8%', '8%', '8%', '10%']
                }
                items={buildTableItems(siniestros)}
                count={count}
                filters={filters}
                setFilters={setFilters}
                isFetching={isLoading}
                legendConfig={ORGANIZATION_LEGEND_CONFIG}
                legendResolver={resolveOrganizationLegendName}
                onSelectRow={() => {}}
                headers={[
                    ...(isSupervisorSIMP
                        ? []
                        : [
                              {
                                  type: 'data',
                                  upperLabel: (item: SiniestroItem) => (
                                      <Checkbox
                                          size="small"
                                          checked={selectedIds.has(Number(item.id))}
                                          disabled={!isSeleccionable(item)}
                                          onChange={() =>
                                              handleToggleSeleccion(Number(item.id), item)
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                      />
                                  )
                              }
                          ]),
                    {
                        type: 'data',
                        // El administrador BO ve de antemano a qué siniestros no se les puede
                        // cargar una visita (SMP-585). La aseguradora viewer no (SMP-628)
                        upperLabel: (item: SiniestroItem) =>
                            isAdminBO && !archivosSrt && !tieneLugarOcurrenciaCompleto(item) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {item?.nroRoam || '-'}
                                    <Tooltip title={t('table.occurrenceLocationIncomplete')}>
                                        <LocationOffOutlinedIcon
                                            data-testid="lugar-ocurrencia-incompleto"
                                            aria-label={t('table.occurrenceLocationIncomplete')}
                                            sx={{ fontSize: 16, color: '#ED6C02' }}
                                        />
                                    </Tooltip>
                                </Box>
                            ) : (
                                item?.nroRoam || '-'
                            )
                    },
                    {
                        type: 'data',
                        upperLabel: (item: SiniestroItem) => item?.empleado?.nombre || '-',
                        lowerLabel: (item: SiniestroItem) => (
                            <Box>
                                <Typography variant="body2">{item?.empleado?.cuil}</Typography>
                            </Box>
                        )
                    },
                    // El Preventor ve el contrato y una columna Organismo con su CUIT (SMP-484).
                    // Contrato muestra solo el CUIT, sin el nombre ni el número (SMP-561).
                    {
                        type: 'data',
                        onClickColumnTitleSortName: 'nroContrato',
                        upperLabel: (item: SiniestroItem) =>
                            renderCuit(
                                isPreventor
                                    ? getContratoOrganismoPreventor(item).contrato.cuit
                                    : item?.cuitContrato
                            ) || '—'
                    },
                    ...(isPreventor
                        ? [
                              {
                                  type: 'data',
                                  upperLabel: (item: SiniestroItem) =>
                                      getContratoOrganismoPreventor(item).organismo.nombre,
                                  lowerLabel: (item: SiniestroItem) =>
                                      renderCuit(getContratoOrganismoPreventor(item).organismo.cuit)
                              }
                          ]
                        : []),
                    {
                        type: 'data',
                        upperLabel: (item: SiniestroItem) => item?.tipo || '-'
                    },
                    ...(isSupervisorSIMP || archivosSrt
                        ? []
                        : [
                              {
                                  type: 'data',
                                  upperLabel: (item: SiniestroItem) => {
                                      const value = getMainPreventor(item);
                                      return value === '-' ? (
                                          <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                              -
                                          </Typography>
                                      ) : (
                                          value
                                      );
                                  }
                              }
                          ]),
                    ...(archivosSrt
                        ? []
                        : [
                              {
                                  type: 'data',
                                  align: 'center',
                                  centerColumn: true,
                                  upperLabel: (item: SiniestroItem) =>
                                      renderVisits(item?.visitasRoam || [], t)
                              }
                          ]),
                    {
                        type: 'data',
                        // TODO: ordenar por fechaAltaRoam cuando el back lo soporte en el sorter.
                        onClickColumnTitleSortName: 'fechaDenuncia',
                        upperLabel: (item: SiniestroItem) =>
                            item.fechaAltaRoam ? getDate(item.fechaAltaRoam) : '-'
                    },
                    ...(isBOUser
                        ? [
                              {
                                  type: 'data' as const,
                                  align: 'center',
                                  centerColumn: true,
                                  upperLabel: (item: SiniestroItem) => (
                                      <ChipVencimientoSRT
                                          fechaVencimientoSRT={item.fechaVencimientoSRT}
                                          fechaPresentacionVisita={item.fechaPresentacionVisita}
                                      />
                                  )
                              }
                          ]
                        : []),
                    ...(archivosSrt
                        ? []
                        : [
                              {
                                  type: 'data',
                                  align: 'center',
                                  centerColumn: true,
                                  upperLabel: (item: SiniestroItem) => renderEstadoChip(item)
                              }
                          ]),
                    {
                        type: 'data',
                        align: 'center',
                        centerColumn: true,
                        upperLabel: (item: SiniestroItem) =>
                            archivosSrt ? (
                                renderEstadoGeneracionArchivo(item.estadoSrt)
                            ) : (
                                <SrtStatusIcon estadoSrt={item.estadoSrt} />
                            )
                    },
                    {
                        type: 'data',
                        align: 'right',
                        centerColumn: true,
                        rightColumn: true,
                        upperLabel: (item: SiniestroItem) => {
                            if (archivosSrt) {
                                return (
                                    <Box
                                        display={'flex'}
                                        gap={1}
                                        justifyContent="flex-end"
                                        alignItems="center"
                                    >
                                        <TableActionButton
                                            onClick={() => archivosSrt.onDescargarTxt(item)}
                                            IconComponent={FileDownloadOutlinedIcon}
                                            disabled={archivosSrt.isDescargando}
                                        />
                                        <TableActionButton
                                            onClick={(event: MouseEvent<HTMLElement>) =>
                                                handleClickAction(event, item)
                                            }
                                            IconComponent={KeyboardArrowDownIcon}
                                        />
                                    </Box>
                                );
                            }

                            const effectiveStatus = activeTab === 'ALL' ? item.estado : activeTab;
                            const hasZipDownload =
                                !!handleDescargarZip &&
                                item.visitasRoam?.some(
                                    (v) => v.estadoId === VisitaEstado.PRESENTADO
                                );
                            const showActionMenu =
                                !isSupervisorSIMP &&
                                (effectiveStatus === 'PENDING' ||
                                    effectiveStatus === 'OPEN' ||
                                    hasZipDownload ||
                                    !isPreventor);

                            return (
                                <Box
                                    display={'flex'}
                                    gap={1}
                                    justifyContent="flex-end"
                                    alignItems="center"
                                >
                                    <TableActionButton
                                        onClick={() =>
                                            navigate(`/${routePrefix}/siniestros/detalle`, {
                                                state: { siniestroId: Number(item.id) }
                                            })
                                        }
                                        IconComponent={VisibilityOutlinedIcon}
                                        disabled={!canViewDetail}
                                    />
                                    <TableActionButton
                                        onClick={() => handleOpenExpressDrawer(item)}
                                        IconComponent={DescriptionOutlinedIcon}
                                        disabled={!canOpenExpressDrawer(item)}
                                    />
                                    {showActionMenu && (
                                        <TableActionButton
                                            onClick={(event: MouseEvent<HTMLElement>) =>
                                                handleClickAction(event, item)
                                            }
                                            IconComponent={KeyboardArrowDownIcon}
                                            disabled={
                                                !isPreventor
                                                    ? false
                                                    : (effectiveStatus === 'OPEN' &&
                                                          !canCreateVisit) ||
                                                      (effectiveStatus === 'PENDING' &&
                                                          !canCreateVisit &&
                                                          !canCancel)
                                            }
                                        />
                                    )}
                                </Box>
                            );
                        }
                    }
                ]}
            />

            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                anchorReference={anchorPosition ? 'anchorPosition' : 'anchorEl'}
                anchorPosition={anchorPosition ?? undefined}
                open={Boolean(anchorPosition || anchorEl) && menuOpen}
                onClose={handleCloseMenu}
            >
                {selectedSiniestro &&
                    (selectedSiniestro.estado === 'PENDING' || selectedSiniestro.estado === 'OPEN') &&
                    canCreateVisit && (
                        <MenuItem onClick={handleCreateVisitFromTable}>
                            + {t('detail.actions.newVisit')}
                        </MenuItem>
                    )}
                {selectedSiniestro && selectedSiniestro.estado === 'PENDING' && canCancel && (
                    <MenuItem onClick={handleOpenCancelDialog}>
                        {t('table.actions.cancelMenu')}
                    </MenuItem>
                )}
                {selectedSiniestro &&
                    !archivosSrt &&
                    handleDescargarZip &&
                    selectedSiniestro.visitasRoam?.some(
                        (v) => v.estadoId === VisitaEstado.PRESENTADO
                    ) && (
                        <MenuItem
                            disabled={isLoadingZip}
                            onClick={() => {
                                handleCloseMenu();
                                handleDescargarZip(selectedSiniestro);
                            }}
                        >
                            Descargar ZIP
                        </MenuItem>
                    )}
                {selectedSiniestro && !isPreventor && !archivosSrt && (
                    <MenuItem onClick={handleOpenHistoryDrawer}>Historial</MenuItem>
                )}
                {selectedSiniestro && archivosSrt && (
                    <MenuItem
                        disabled={!canViewDetail}
                        onClick={() => {
                            handleCloseMenu();
                            navigate(`/${routePrefix}/siniestros/detalle`, {
                                state: {
                                    siniestroId: Number(selectedSiniestro.id),
                                    ...archivosSrt.detalleState
                                }
                            });
                        }}
                    >
                        {t('table.actions.seeSiniestro')}
                    </MenuItem>
                )}
                {selectedSiniestro && archivosSrt && (
                    <MenuItem
                        onClick={() => {
                            handleCloseMenu();
                            archivosSrt.onDescargarInforme(selectedSiniestro);
                        }}
                    >
                        {t('table.actions.downloadInvestigationReport')}
                    </MenuItem>
                )}
                {selectedSiniestro && archivosSrt && (
                    <MenuItem
                        disabled={!getVisitaPresentada(selectedSiniestro)}
                        onClick={() => {
                            handleCloseMenu();
                            archivosSrt.onDescargarActa(selectedSiniestro);
                        }}
                    >
                        {t('table.actions.downloadVisitActa')}
                    </MenuItem>
                )}
            </Menu>

            {cancelDialogOpen && (
                <AlertDialog
                    title={t('table.cancelDialog.title')}
                    message={t('table.cancelDialog.message', {
                        id: selectedSiniestro?.nroSiniestro || selectedSiniestro?.id || '-'
                    })}
                    icon="cancel"
                    closeAction={handleCloseCancelDialog}
                    reactElement={
                        <TextField
                            select
                            fullWidth
                            label={t('table.cancelDialog.fieldLabel')}
                            value={cancelReason}
                            onChange={(event) => {
                                setCancelReason(Number(event.target.value));
                                setCancelReasonError(false);
                            }}
                            error={cancelReasonError}
                            helperText={
                                cancelReasonError ? t('table.cancelDialog.validation') : ' '
                            }
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value="" disabled>
                                {t('table.cancelDialog.placeholder')}
                            </MenuItem>
                            {cancelReasonOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.descripcion}
                                </MenuItem>
                            ))}
                        </TextField>
                    }
                    customButtons={
                        <Box display="flex" gap={2} width="100%">
                            <Button variant="outlined" fullWidth onClick={handleCloseCancelDialog}>
                                {t('table.cancelDialog.back')}
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                color="error"
                                onClick={handleCancelSiniestro}
                                disabled={isCancelling}
                            >
                                {t('table.cancelDialog.confirm')}
                            </Button>
                        </Box>
                    }
                />
            )}

            {!archivosSrt && (
                <>
                    <ExpressVisitManagementDrawer
                        open={expressDrawerOpen}
                        onClose={handleCloseExpressDrawer}
                        siniestro={selectedSiniestro}
                        activeTab={activeTab}
                        onSuccess={onRefresh}
                    />

                    <SiniestroHistoryDrawer
                        open={historyDrawerOpen}
                        onClose={handleCloseHistoryDrawer}
                        siniestro={selectedSiniestro}
                    />
                </>
            )}

            <BackdropComponent loading={isCancelling} />
        </>
    );
}

export default SiniestrosTable;
